import { useTranslation } from 'react-i18next'

import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

interface PondEnd {
  pondName: string
  farmName: string
  /** Marks the farm as the one the user is working in: "(this farm)". */
  isCurrentFarm?: boolean
}

interface TransferConfirmDialogProps {
  open: boolean
  /** Another farm gets its own title; a move inside the farm names the pond. */
  crossFarm: boolean
  from: PondEnd
  to: PondEnd
  /** Pre-formatted "500 kg · ≈ N pcs · ฿X · opens a new cycle at pond Y". */
  summary: string
  loading?: boolean
  onCancel: () => void
  onConfirm: () => void
}

const TransferConfirmDialog = ({
  open,
  crossFarm,
  from,
  to,
  summary,
  loading,
  onCancel,
  onConfirm,
}: TransferConfirmDialogProps) => {
  const { t } = useTranslation(['shrimpTransfer', 'common'])

  const renderEnd = (label: string, end: PondEnd, highlighted = false) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        borderRadius: 2,
        border: 2,
        borderColor: highlighted ? 'green.500' : 'transparent',
        bgcolor: highlighted ? 'action.tonalBg' : 'grey.100',
      }}
    >
      <Typography variant="body2" color={highlighted ? 'action.tonalFg' : 'text.secondary'}>
        {label}
      </Typography>
      <Typography variant="h6" color={highlighted ? 'action.tonalFg' : 'text.primary'}>
        {t('transfer.pondLabel', { name: end.pondName })}
      </Typography>
      <Typography variant="body2" color={highlighted ? 'action.tonalFg' : 'text.secondary'}>
        {`${t('transfer.farmLabel', { name: end.farmName })}${
          end.isCurrentFarm ? ` ${t('transfer.thisFarm')}` : ''
        }`}
      </Typography>
    </Box>
  )

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {crossFarm
          ? t('transfer.confirmCrossTitle')
          : t('transfer.confirmSameTitle', {
              pond: t('transfer.pondLabel', { name: to.pondName }),
            })}
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          {renderEnd(t('transfer.fromShort'), from)}
          <ArrowForwardIcon sx={{ color: 'text.secondary' }} />
          {renderEnd(t('transfer.toShort'), to, true)}
        </Stack>
        <Typography color="text.secondary">{summary}</Typography>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" color="inherit" onClick={onCancel} disabled={loading}>
          {t('common:cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={onConfirm}
          loading={loading}
          sx={{
            bgcolor: 'action.primaryBg',
            color: 'action.primaryOn',
            '&:hover': { bgcolor: 'action.primaryHover' },
            '&:active': { bgcolor: 'action.primaryPressed' },
          }}
        >
          {t('transfer.confirmMove')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default TransferConfirmDialog
